import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationService } from 'src/app/core/services/notification.service';
import { PageHeaderComponent } from 'src/app/shared/components/page-header/page-header.component';
import { ContributionUserService } from 'src/app/shared/services/contribution-user.service';
import {
  Contribution,
  ContributionStatus,
} from 'src/app/shared/models/contribution.models';

@Component({
  selector: 'app-user-contribution-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    PageHeaderComponent,
    ButtonModule,
    CardModule,
    TagModule,
    ToastModule,
    TableModule,
    TooltipModule,
  ],
  templateUrl: './contribution-detail.component.html',
})
export class UserContributionDetailComponent implements OnInit {
  private contributionService = inject(ContributionUserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);

  contribution: Contribution | null = null;
  loading = true;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadContribution(id);
    }
  }

  loadContribution(id: string): void {
    this.loading = true;
    this.contributionService.getContribution(id).subscribe({
      next: (response) => {
        this.contribution = response.data;
        this.loading = false;
      },
      error: (err) => {
        if (err.status === 403) {
          this.notificationService.error(
            "You don't have access to this contribution",
          );
        } else if (err.status === 404) {
          this.notificationService.error('Contribution not found');
        } else {
          this.notificationService.error('Failed to load contribution');
        }
        this.loading = false;
        this.router.navigate(['/home/contributions']);
      },
    });
  }

  backToList(): void {
    this.router.navigate(['/home/contributions']);
  }

  getStatusClasses(status: ContributionStatus): string {
    const classMap: Record<ContributionStatus, string> = {
      [ContributionStatus.PENDING_PAYMENT]: 'bg-yellow-100 text-yellow-700',
      [ContributionStatus.PAID]: 'bg-green-100 text-green-700',
      [ContributionStatus.FAILED]: 'bg-red-100 text-red-700',
      [ContributionStatus.CANCELED]: 'bg-gray-100 text-gray-700',
      [ContributionStatus.REFUNDED]: 'bg-blue-100 text-blue-700',
    };
    return classMap[status] || 'bg-gray-100 text-gray-700';
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatAmount(amount: number | string, showVatLabel = false): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return showVatLabel ? `€${numAmount.toFixed(2)} (excl. VAT)` : `€${numAmount.toFixed(2)}`;
  }

  downloadCertificate(): void {
    if (!this.contribution || this.contribution.status !== ContributionStatus.PAID) {
      this.notificationService.error('Certificate only available for paid contributions');
      return;
    }
    const url = this.contributionService.getCertificateUrl(this.contribution.id);
    window.open(url, '_blank');
  }

  downloadInvoice(): void {
    if (!this.contribution) return;

    // Allow invoice download for PAID or for BANK_TRANSFER with invoiceS3Key (proforma)
    const isPaid = this.contribution.status === ContributionStatus.PAID;
    const isBankTransferWithInvoice =
      this.contribution.paymentMethod === 'BANK_TRANSFER' && this.contribution.invoiceS3Key;

    if (!isPaid && !isBankTransferWithInvoice) {
      return;
    }

    if (!this.contribution.invoiceS3Key) {
      this.notificationService.info('Invoice is being generated, please try again shortly');
      return;
    }
    const url = this.contributionService.getInvoiceUrl(this.contribution.id);
    window.open(url, '_blank');
  }

  hasInvoice(): boolean {
    return !!this.contribution?.invoiceS3Key;
  }

  getTotalCO2(): number {
    if (!this.contribution) return 0;
    return this.contribution.projectContributions.reduce(
      (sum, pc) => sum + (typeof pc.co2Compensated === 'string' ? parseFloat(pc.co2Compensated) : pc.co2Compensated),
      0,
    );
  }

  getShortId(): string {
    return this.contribution?.id.substring(0, 8) || '';
  }

  canDownload(): boolean {
    return this.contribution?.status === ContributionStatus.PAID;
  }

  copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      this.notificationService.success('Copied to clipboard');
    });
  }

  isPaid(): boolean {
    return this.contribution?.status === ContributionStatus.PAID;
  }

  getProjectName(): string {
    if (!this.contribution?.projectContributions?.length) return '';
    return this.contribution.projectContributions[0].projectName;
  }

  getProjectPricePerTonne(): number {
    if (!this.contribution?.projectContributions?.length) return 0;
    const price = this.contribution.projectContributions[0].pricePerTonne;
    return typeof price === 'string' ? parseFloat(price) : price;
  }

  getSubtotal(): number {
    if (!this.contribution) return 0;
    // Subtotal = Total - VAT (totalAmount includes VAT)
    const total = typeof this.contribution.totalAmount === 'string'
      ? parseFloat(this.contribution.totalAmount)
      : this.contribution.totalAmount;
    // VAT is 21%, so subtotal = total / 1.21
    return total / 1.21;
  }

  getVatAmount(): number {
    if (!this.contribution) return 0;
    const total = typeof this.contribution.totalAmount === 'string'
      ? parseFloat(this.contribution.totalAmount)
      : this.contribution.totalAmount;
    return total - (total / 1.21);
  }
}
